import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSelectedStore } from '@/contexts/StoreContext';
import { useAppointments, useStore } from '@/hooks';
import { useWeeklyOperatingHours } from '@/hooks/useWeeklyOperatingHours';
import { useSpecialDateOperatingHours } from '@/hooks/useSpecialDateOperatingHours';
import { generateAvailableDates, getTimeSlotsForDate, getParallelAppointmentsForDate } from '@/lib/dashboard-utils';
import type { Metrics } from '../components/MetricsCards';
import { ALL_DATES_VALUE } from '../components/DateFilter';

export function useDashboardData() {
  const { user } = useAuth();
  const { selectedStoreId } = useSelectedStore();

  const isStoreAdmin = user?.role === 'store_admin';
  const userStoreId = isStoreAdmin ? selectedStoreId : null;

  // Load store data for store_admin
  const { data: userStore } = useStore(userStoreId || '');

  // Load weekly and special date operating hours for store_admin
  const { data: weeklyHours = [] } = useWeeklyOperatingHours(
    userStoreId || undefined,
    { enabled: isStoreAdmin }
  );

  const { data: specialDateHours = [] } = useSpecialDateOperatingHours(
    userStoreId || undefined,
    { enabled: isStoreAdmin }
  );

  // Admin loads all appointments, store_admin loads only their store's
  const { data: appointments = [], isLoading: isLoadingAppointments } = useAppointments(
    userStoreId ? { storeId: userStoreId } : undefined
  );

  // Get available dates based on user role
  const availableDates = useMemo(() => {
    if (isStoreAdmin && userStoreId) {
      // For store_admin: generate dates using cascade logic (max 60 days = 2 months)
      return generateAvailableDates(userStoreId, weeklyHours, specialDateHours, 60);
    }

    // For admin: get unique dates from all appointments
    const uniqueDates = [...new Set(appointments.map((apt) => apt.date))];
    return uniqueDates.sort();
  }, [isStoreAdmin, userStoreId, weeklyHours, specialDateHours, appointments]);

  // Selected date state - initialized when availableDates loads
  const [selectedDate, setSelectedDate] = useState<string>('');

  // Sync selectedDate with availableDates
  // Reset to first available date when dates change and current selection is invalid
  useEffect(() => {
    if (availableDates.length === 0) {
      setSelectedDate('');
      return;
    }

    // Don't reset if "all" is selected - it's always valid
    if (selectedDate === ALL_DATES_VALUE) {
      return;
    }

    // If no date selected or current date is not in available dates, select first
    if (!selectedDate || !availableDates.includes(selectedDate)) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates, selectedDate]);

  // Filter appointments by selected date
  const filteredAppointments = useMemo(() => {
    if (!selectedDate) return [];

    const isAllDates = selectedDate === ALL_DATES_VALUE;

    return appointments.filter((apt) => {
      const dateMatch = isAllDates || apt.date === selectedDate;
      const storeMatch = userStoreId ? apt.storeId === userStoreId : true;
      return dateMatch && storeMatch;
    });
  }, [appointments, selectedDate, userStoreId]);

  // Calculate metrics from filtered appointments
  const metrics: Metrics = useMemo(() => {
    const pending = filteredAppointments.filter((a) => a.status === 'pending').length;
    const completed = filteredAppointments.filter((a) => a.status === 'completed').length;
    const cancelled = filteredAppointments.filter((a) => a.status === 'cancelled').length;
    const total = filteredAppointments.length;

    return { total, pending, completed, cancelled };
  }, [filteredAppointments]);

  // Get time slots for store agenda using cascade logic
  const timeSlots = useMemo(() => {
    if (!userStoreId || !selectedDate || selectedDate === ALL_DATES_VALUE) return [];

    return getTimeSlotsForDate(selectedDate, userStoreId, weeklyHours, specialDateHours, userStore || null);
  }, [userStoreId, selectedDate, weeklyHours, specialDateHours, userStore]);

  // Get max parallel appointments for the selected date using cascade logic
  const maxParallelAppointments = useMemo(() => {
    if (!userStoreId || !selectedDate || selectedDate === ALL_DATES_VALUE) {
      return userStore?.parallelAppointments || 1;
    }

    return getParallelAppointmentsForDate(
      selectedDate,
      userStoreId,
      weeklyHours,
      specialDateHours,
      userStore?.parallelAppointments || 1
    );
  }, [userStoreId, selectedDate, weeklyHours, specialDateHours, userStore]);

  return {
    user,
    isStoreAdmin,
    userStore,
    isLoading: isLoadingAppointments,

    // Date filter
    availableDates,
    selectedDate,
    setSelectedDate,

    // Data
    filteredAppointments,
    metrics,
    timeSlots,
    maxParallelAppointments,
  };
}
